import React, { useMemo } from "react";
import { FiAlertTriangle, FiCheckCircle, FiSearch } from "react-icons/fi";
import { LoadingSpinner } from "../LoadingSpinner";
import { EmptyState } from "./BrowserView";
import type { UsageScanState } from "./types";
import { formatFileSize } from "./utils";

export interface OrphansViewProps {
	usage: UsageScanState;
	onRequestScan: () => void;
	/** Ouvre le panneau de détail du fichier (chargé par id via ?file=). */
	onOpen: (id: string) => void;
}

/**
 * Vue « orphelins » : les fichiers du stockage qu'aucun contenu ne référence, d'après le rapport
 * du scan (POST /api/storage/orphans/scan) : le même rapport, mis en cache, que la section
 * « Utilisé par » du panneau de détail. Cliquer une ligne ouvre le détail du fichier, où la
 * suppression reste confirmée individuellement.
 */
export const OrphansView: React.FC<OrphansViewProps> = ({ usage, onRequestScan, onOpen }) => {
	const orphans = useMemo(
		() => (usage.report?.files ?? []).filter((file) => file.verdict === "orphan"),
		[usage.report]
	);

	if (usage.loading) {
		return (
			<div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-6 py-10 text-sm text-slate-500">
				<LoadingSpinner size="sm" inline /> Analyse de la médiathèque en cours…
			</div>
		);
	}

	if (usage.error) {
		return (
			<p className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">
				{usage.error}
			</p>
		);
	}

	if (!usage.report) {
		return (
			<EmptyState
				icon={<FiAlertTriangle aria-hidden />}
				title="Trouver les fichiers que plus aucun contenu ne référence."
				hint="L'analyse parcourt tout le stockage et tous les contenus : le résultat est gardé en mémoire pour la session."
			>
				<button
					type="button"
					onClick={onRequestScan}
					className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#005CA9] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#004a87]"
				>
					<FiSearch className="h-4 w-4" /> Analyser la médiathèque
				</button>
			</EmptyState>
		);
	}

	const report = usage.report;

	return (
		<div className="space-y-3">
			<div className="flex flex-wrap items-center gap-2">
				<span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600">
					{Number(report.totalFiles ?? 0).toLocaleString("fr-FR")} fichiers analysés
				</span>
				<span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
					<FiCheckCircle className="h-3.5 w-3.5" aria-hidden /> {Number(report.usedCount ?? 0).toLocaleString("fr-FR")} utilisés
				</span>
				<span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700">
					<FiAlertTriangle className="h-3.5 w-3.5" aria-hidden /> {orphans.length.toLocaleString("fr-FR")} orphelins ·{" "}
					{formatFileSize(Number(report.orphanBytes ?? 0))}
				</span>
				<button
					type="button"
					onClick={onRequestScan}
					className="ml-auto rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
				>
					Relancer l'analyse
				</button>
			</div>

			{orphans.length === 0 ? (
				<EmptyState
					icon={<FiCheckCircle aria-hidden />}
					title="Aucun fichier orphelin : toute la médiathèque est référencée."
					hint="Relancez l'analyse après des suppressions de contenus pour garder ce verdict à jour."
				/>
			) : (
				<div className="overflow-x-auto rounded-xl border border-slate-200">
					<table className="min-w-full divide-y divide-slate-200 text-sm">
						<thead className="bg-slate-50">
							<tr>
								<th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
									Clé
								</th>
								<th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
									Dossier
								</th>
								<th scope="col" className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
									Taille
								</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-slate-100 bg-white">
							{orphans.map((file) => (
								<tr
									key={file.id ?? file.key ?? ""}
									onClick={() => file.id && onOpen(file.id)}
									className={`transition hover:bg-slate-50 ${file.id ? "cursor-pointer" : ""}`}
								>
									<td className="max-w-[28rem] truncate px-4 py-2.5 font-mono text-xs text-slate-700" title={file.key ?? undefined}>
										{file.key ?? "-"}
									</td>
									<td className="whitespace-nowrap px-4 py-2.5 text-xs text-slate-500">{file.folder ?? "-"}</td>
									<td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums text-slate-700">{formatFileSize(file.size)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			<p className="text-[11px] text-slate-400">
				La suppression se fait fichier par fichier depuis le panneau de détail (cliquez une ligne), avec confirmation.
			</p>
		</div>
	);
};
